import { ChatHistorySidebarBase, type SidebarSession } from "@/components/shared/ChatHistorySidebarBase"
import { useAssistantStore } from "@/stores/assistant-store"
import { assistantThreadTitle } from "@/lib/assistant-thread-title"

interface ChatHistorySidebarProps {
  isExpanded: boolean
  onExpandedChange: (expanded: boolean) => void
  /** Fill the host (the assistant dock's history view) instead of a right-hand rail. */
  fill?: boolean
  /** Called after a conversation is chosen or a new one started (the dock returns to the chat). */
  onSelected?: () => void
  /** Full-page routing owns selection; the dock keeps the store defaults. */
  onSelectThread?: (threadId: string) => void
  onNewChat?: () => void
}

/**
 * Assistant chat history = agent THREADS within the org chat session. Each row
 * can be renamed, pinned/unpinned and deleted (delete is confirm-gated inside
 * the base component); labels come from the thread name or, until a thread is
 * named, the first-message preview.
 */
export function ChatHistorySidebar({ isExpanded, onExpandedChange, fill = false, onSelected, onSelectThread, onNewChat }: ChatHistorySidebarProps) {
  const {
    sessionId,
    error,
    threads,
    activeThreadId,
    threadPreviews,
    startNewChat,
    selectThread,
    renameThread,
    togglePinThread,
    deleteThread,
  } = useAssistantStore()

  // No session yet means the first fetch has not settled: the thread list
  // arrives with the session. A failed fetch ends the wait (the page shows the
  // error), so the spinner never hangs.
  const isListLoading = sessionId === null && error === null

  const sessions: SidebarSession[] = threads.map((thread) => ({
    id: thread.id,
    title: assistantThreadTitle(thread.name, threadPreviews[thread.id]),
    updatedAt: thread.updatedAt,
    createdAt: thread.createdAt,
    userId: thread.createdBy,
    organizationId: '',
    messageCount: 0,
    lastMessageAt: null,
    isPinned: thread.isPinned,
  }))

  return (
    <ChatHistorySidebarBase
      isExpanded={isExpanded}
      onExpandedChange={onExpandedChange}
      sessions={sessions}
      currentSessionId={activeThreadId}
      onNewChat={() => {
        if (onNewChat) onNewChat()
        else void startNewChat()
        onSelected?.()
      }}
      onSelectSession={(threadId) => {
        if (onSelectThread) onSelectThread(threadId)
        else void selectThread(threadId)
        onSelected?.()
      }}
      onRenameSession={(threadId, newTitle) => renameThread(threadId, newTitle)}
      onTogglePin={(threadId) => togglePinThread(threadId)}
      onDeleteSession={(threadId) => deleteThread(threadId)}
      headerTitle="Conversations"
      emptyText="No conversations yet"
      emptyActionText="Start a new chat"
      isLoading={isListLoading}
      renameDialogTitle="Rename conversation"
      renameDialogDescription="Give this conversation a name you can find later."
      renameInputLabel="Name"
      renameInputPlaceholder="e.g. Pricing brainstorm"
      fill={fill}
    />
  )
}
